"use client";
import { useEffect, useMemo, useRef, useState } from "react";

import PageShell from "@/components/PageShell";
import { Flag } from "@/components/ui/Flag";
import { saveBlob } from "@/lib/download";
import { setScanning, take as takeVioHandoff } from "@/lib/vio/handoff";

const MIN_CHARS = 100;
const MAX_CHARS = 30000;
const INITIAL_CLAUSE_LIMIT = 5;

type RiskLevel = "red" | "yellow" | "green";

type Clause = {
  text: string;
  risk_level: RiskLevel;
  risk_score: number;
  explanation: string;
  confidence: number;
  clause_type: string;
  india_law_ref: string | null;
};

type ScanResult = {
  overall_risk_score: number;
  executive_summary: string;
  contract_type: string;
  jurisdiction: string;
  clauses: Clause[];
  disclaimer: string;
};

type ScanResponse = {
  result: ScanResult;
  cached: boolean;
  // `offer` is the joining offer the sign-up path actually redeems, worded by
  // the backend (public/scan.py::_cta); absent when no offer is live. It
  // replaced `free_plan` ("10 contracts/month - No credit card"), which no
  // plan ever granted.
  upgrade_cta: { message: string; url: string; offer?: string };
};

type Language = { value: string; label: string; native: string; flag: string };
type Jurisdiction = { value: string; label: string; flag: string; primary?: boolean };
type PromptTemplate = { id: string; label: string; hint: string; icon: string; sample: string };
type OpenMenu = "prompts" | "sources" | "settings" | null;
type FilterMode = "all" | "red" | "yellow" | "green";
type SortMode = "risk" | "order" | "confidence";

const LANGUAGES: Language[] = [
  { value: "en", label: "English",  native: "English",   flag: "🇬🇧" },
];

// `flag` is an ISO 3166-1 alpha-2 code (lowercase) used with flagcdn.com.
// UK is non-standard for flagcdn; we keep it as the display code but map to "gb".
const JURISDICTIONS: Jurisdiction[] = [
  { value: "US", label: "United States",      flag: "us", primary: true },
  { value: "UK", label: "United Kingdom",     flag: "gb" },
  { value: "EU", label: "European Union",     flag: "eu" },
  { value: "AU", label: "Australia",          flag: "au" },
  { value: "SG", label: "Singapore",          flag: "sg" },
  { value: "AE", label: "United Arab Emirates", flag: "ae" },
  { value: "CA", label: "Canada",             flag: "ca" },
  // "IN" deliberately absent: this is the GLOBAL site's scanner, and §1A
  // (GLOBAL_BACKEND_PLAN_2026-08.md) bars Indian analysis surfaces here.
  // India's own site (lexvio.ai) is where an Indian-law scan lives.
  { value: "DE", label: "Germany",            flag: "de" },
  { value: "FR", label: "France",             flag: "fr" },
  { value: "NL", label: "Netherlands",        flag: "nl" },
  { value: "IE", label: "Ireland",            flag: "ie" },
  { value: "CH", label: "Switzerland",        flag: "ch" },
  { value: "SE", label: "Sweden",             flag: "se" },
  { value: "NO", label: "Norway",             flag: "no" },
  { value: "DK", label: "Denmark",            flag: "dk" },
  { value: "FI", label: "Finland",            flag: "fi" },
  { value: "ES", label: "Spain",              flag: "es" },
  { value: "IT", label: "Italy",              flag: "it" },
  { value: "PT", label: "Portugal",           flag: "pt" },
  { value: "BE", label: "Belgium",            flag: "be" },
  { value: "AT", label: "Austria",            flag: "at" },
  { value: "PL", label: "Poland",             flag: "pl" },
  { value: "CZ", label: "Czech Republic",     flag: "cz" },
  { value: "JP", label: "Japan",              flag: "jp" },
  { value: "KR", label: "South Korea",        flag: "kr" },
  { value: "HK", label: "Hong Kong",          flag: "hk" },
  { value: "CN", label: "China",              flag: "cn" },
  { value: "MY", label: "Malaysia",           flag: "my" },
  { value: "TH", label: "Thailand",           flag: "th" },
  { value: "ID", label: "Indonesia",          flag: "id" },
  { value: "PH", label: "Philippines",        flag: "ph" },
  { value: "VN", label: "Vietnam",            flag: "vn" },
  { value: "PK", label: "Pakistan",           flag: "pk" },
  { value: "BD", label: "Bangladesh",         flag: "bd" },
  { value: "LK", label: "Sri Lanka",          flag: "lk" },
  { value: "NP", label: "Nepal",              flag: "np" },
  { value: "BR", label: "Brazil",             flag: "br" },
  { value: "MX", label: "Mexico",             flag: "mx" },
  { value: "ZA", label: "South Africa",       flag: "za" },
  { value: "NG", label: "Nigeria",            flag: "ng" },
  { value: "KE", label: "Kenya",              flag: "ke" },
  { value: "NZ", label: "New Zealand",        flag: "nz" },
  { value: "IL", label: "Israel",             flag: "il" },
  { value: "SA", label: "Saudi Arabia",       flag: "sa" },
  { value: "QA", label: "Qatar",              flag: "qa" },
  { value: "TR", label: "Turkey",             flag: "tr" },
];

const PROMPT_TEMPLATES: PromptTemplate[] = [
  {
    id: "nda",
    label: "Mutual NDA",
    hint: "Non-disclosure — five-year term",
    icon: "🔒",
    sample:
      "This Mutual Non-Disclosure Agreement is entered into between Acme Corp (\"Disclosing Party\") and the Receiving Party. The parties agree that all Confidential Information disclosed shall be kept secret for a period of five (5) years from the date of disclosure. The receiving party shall not use such Confidential Information for any purpose other than the evaluation of a potential business relationship. Any breach of this Agreement shall entitle the disclosing party to seek injunctive relief and monetary damages without the need to prove actual loss. This Agreement shall be governed by the laws of the state of Delaware, and the courts located therein shall have exclusive jurisdiction. The obligations of confidentiality shall survive termination of this Agreement indefinitely. Either party may terminate this Agreement upon thirty (30) days written notice.",
  },
  {
    id: "employ",
    label: "Employment offer",
    hint: "Full-time role with non-compete + IP assignment",
    icon: "💼",
    sample:
      "This Employment Agreement is made between ABC Technologies Inc. and the Employee. The Employee shall serve as Software Engineer on a probation period of six (6) months, extendable at the sole discretion of the Company. During the probation period, either party may terminate the employment with seven (7) days notice without assigning any reason. The Employee agrees to a non-compete clause restricting employment with any competing firm for twelve (12) months after termination. The Employee shall not solicit any client or employee of the Company for eighteen (18) months post-termination. All intellectual property created during the course of employment, whether on company time, using company resources, or otherwise, shall be the exclusive property of the Company. The Employee waives all moral rights. Salary shall be paid in accordance with the Company's payroll schedule, subject to statutory deductions.",
  },
  {
    id: "saas",
    label: "SaaS terms",
    hint: "Software-as-a-service — auto-renewal, arbitration",
    icon: "☁️",
    sample:
      "These Terms of Service govern your access to and use of the Service provided by XYZ Software Inc. By accessing the Service, you agree to these Terms. The Service is provided on an \"as-is\" and \"as-available\" basis, without warranties of any kind, express or implied. In no event shall the Company's total aggregate liability exceed the fees paid by you in the three (3) months immediately preceding the claim. The Company may suspend or terminate your access at any time, with or without cause, and without prior notice, and shall not be liable to you or any third party for such suspension. All fees are non-refundable. This Agreement automatically renews for successive one-year terms unless cancelled by the Customer at least thirty (30) days before the renewal date. You agree to indemnify the Company against all claims arising from your use of the Service. Disputes shall be resolved by binding arbitration in Delaware, USA.",
  },
  {
    id: "rental",
    label: "Rental agreement",
    hint: "Residential lease — 12-month tenancy",
    icon: "🏠",
    sample:
      "This Residential Lease Agreement is entered into between the Landlord and the Tenant for the residential premises situated at the address specified in Schedule A. The lease shall be for a period of twelve (12) months, commencing from the effective date. The monthly rent shall be $1,800 payable in advance by the 5th of each calendar month, subject to an escalation of 5% on renewal. A refundable security deposit of $3,600 (equivalent to approximately two months' rent) shall be paid on execution. The deposit shall be refunded within fifteen (15) days of the Tenant vacating, subject to deductions for damages beyond normal wear and tear, outstanding utility bills, and any unpaid dues. The Tenant shall not sublet the premises or use them for any commercial purpose. Either party may terminate this agreement with two (2) months' written notice. The Tenant shall bear all utility charges including electricity, water, and maintenance fees.",
  },
];

const PIPELINE_STEPS = [
  { id: 1, label: "Parsing document structure",        endPct: 18 },
  { id: 2, label: "Identifying clauses & obligations", endPct: 40 },
  { id: 3, label: "Reading the governing-law clause",   endPct: 65 },
  { id: 4, label: "Computing per-clause risk scores",  endPct: 85 },
  { id: 5, label: "Drafting executive summary",        endPct: 100 },
] as const;

const REASONING_LINES = [
  "Parsing paragraphs into atomic clauses…",
  "Identifying indemnity and limitation-of-liability terms…",
  "Reading the clauses against the governing law…",
  "Flagging one-sided obligations and auto-renewal…",
  "Checking termination windows and notice periods…",
  "Evaluating governing-law and venue clauses…",
  "Scoring confidence per finding…",
  "Drafting plain-English explanations…",
];

function riskColor(level: RiskLevel): string {
  if (level === "red") return "var(--red)";
  if (level === "yellow") return "var(--amber)";
  return "var(--green)";
}

function scoreColor(score: number): string {
  if (score >= 67) return "var(--red)";
  if (score >= 34) return "var(--amber)";
  return "var(--green)";
}

function scoreVerdict(score: number): string {
  if (score >= 67) return "High-risk contract.";
  if (score >= 34) return "Moderate risk detected.";
  return "Looks reasonable.";
}

function detectContractType(text: string): string | null {
  if (text.length < 120) return null;
  const t = text.toLowerCase();
  if (t.includes("non-disclosure") || t.includes("confidentiality agreement") || t.includes("mutual nda")) return "Non-disclosure";
  if (t.includes("leave and license") || t.includes("lease deed") || t.includes("tenancy")) return "Rental / lease";
  if (t.includes("employment") && (t.includes("probation") || t.includes("salary") || t.includes("non-compete"))) return "Employment";
  if (t.includes("terms of service") || t.includes("terms and conditions") || (t.includes("saas") && t.includes("service"))) return "SaaS / ToS";
  if (t.includes("master service") || t.includes(" msa ") || t.includes("statement of work")) return "Master services";
  if (t.includes("consulting") || t.includes("freelance") || t.includes("independent contractor")) return "Consulting";
  if (t.includes("purchase order") || t.includes("supply agreement") || t.includes("procurement")) return "Supply";
  if (t.includes("shareholders") && (t.includes("founders") || t.includes("share purchase"))) return "Shareholder";
  if (t.includes("loan agreement") || t.includes("promissory note") || t.includes("debenture")) return "Loan";
  if (t.includes("assignment") && t.includes("intellectual property")) return "IP assignment";
  return "Contract";
}

/* ========================================================================= */
/* useCountUp — animated integer counter                                      */
/* ========================================================================= */
function useCountUp(target: number, duration = 1100, delay = 0): number {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let frame = 0;
    let mounted = true;
    const timer = setTimeout(() => {
      const t0 = performance.now();
      const tick = (now: number) => {
        if (!mounted) return;
        const p = Math.min(1, (now - t0) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        setVal(Math.round(target * eased));
        if (p < 1) frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    }, delay);
    return () => {
      mounted = false;
      clearTimeout(timer);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [target, duration, delay]);
  return val;
}

/* ========================================================================= */
/* RiskGauge — animated SVG radial fill + counted number                      */
/* ========================================================================= */
function RiskGauge({ score, size = 132 }: { score: number; size?: number }) {
  const counted = useCountUp(score, 1400, 120);
  const r = size / 2 - 8;
  const c = 2 * Math.PI * r;
  const color = scoreColor(score);
  const offset = c - (counted / 100) * c;
  return (
    <svg aria-label={`Overall risk ${score} out of 100`} className="scan-gauge" height={size} viewBox={`0 0 ${size} ${size}`} width={size}>
      <circle cx={size / 2} cy={size / 2} fill="none" r={r} stroke="var(--b200)" strokeWidth="6" />
      <circle
        cx={size / 2} cy={size / 2} fill="none"
        r={r} stroke={color} strokeDasharray={c}
        strokeDashoffset={offset}
        strokeLinecap="round"
        strokeWidth="6"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        dominantBaseline="central" fill={color}
        fontSize={size * 0.3} fontWeight={200}
        style={{ fontFamily: "var(--font-fraunces), serif" }} textAnchor="middle" x={size / 2} y={size / 2 - 2}
      >{counted}</text>
      <text
        fill="rgba(19,18,16,.5)" fontSize="10"
        letterSpacing=".14em"
        textAnchor="middle" x={size / 2} y={size / 2 + size * 0.22}
      >/ 100</text>
    </svg>
  );
}

/* ========================================================================= */
/* ReasoningTicker — rotating "thinking" text during scan                     */
/* ========================================================================= */
function ReasoningTicker() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((v) => (v + 1) % REASONING_LINES.length), 1900);
    return () => clearInterval(id);
  }, []);
  return (
    <div aria-live="polite" className="scan-reasoning">
      <span aria-hidden="true" className="scan-reasoning-dot" />
      <span key={i} className="scan-reasoning-text">{REASONING_LINES[i]}</span>
    </div>
  );
}

/* ========================================================================= */
/* Pipeline — step-through progress during scan                               */
/* ========================================================================= */
function Pipeline() {
  const [currentStep, setCurrentStep] = useState(0);
  const [pct, setPct] = useState(4);

  useEffect(() => {
    const stepDurations = [2500, 3100, 3500, 2900, 2000];
    const timers: ReturnType<typeof setTimeout>[] = [];
    let accum = 0;
    PIPELINE_STEPS.forEach((step, i) => {
      const startTime = accum;
      const duration = stepDurations[i];
      if (i > 0) timers.push(setTimeout(() => setCurrentStep(i), startTime));
      timers.push(setTimeout(() => setPct(step.endPct), startTime + duration * 0.8));
      accum = startTime + duration;
    });
    timers.push(setTimeout(() => setPct(96), accum));
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="scan-pipeline" id="scan-progress">
      <div className="scan-pipeline-head">
        <div className="scan-pipeline-title">
          Analysing contract<span className="scan-dots"><span>.</span><span>.</span><span>.</span></span>
        </div>
        <div className="scan-pipeline-pct">{Math.round(pct)}%</div>
      </div>
      <div className="scan-pipeline-bar">
        <div className="scan-pipeline-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="scan-pipeline-steps">
        {PIPELINE_STEPS.map((s, i) => {
          const state = i < currentStep ? "done" : i === currentStep ? "active" : "pending";
          return (
            <div key={s.id} className={`scan-pipeline-step is-${state}`}>
              <div aria-hidden="true" className="scan-pipeline-step-dot">
                {state === "done" ? (
                  <svg fill="none" height="10" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" viewBox="0 0 10 10" width="10"><path d="M2 5l2 2 4-4.5" /></svg>
                ) : state === "active" ? (
                  <span className="scan-pipeline-dot-pulse" />
                ) : (
                  i + 1
                )}
              </div>
              <div className="scan-pipeline-step-label">{s.label}</div>
              <div className="scan-pipeline-step-status">
                {state === "done" ? "Done" : state === "active" ? "Running" : "Queued"}
              </div>
            </div>
          );
        })}
      </div>
      <ReasoningTicker />
    </div>
  );
}

/* ========================================================================= */
/* RiskDistribution — clickable stacked bar                                   */
/* ========================================================================= */
function RiskDistribution({
  clauses,
  active,
  onSelect,
}: {
  clauses: Clause[];
  active: FilterMode;
  onSelect: (level: FilterMode) => void;
}) {
  const total = Math.max(clauses.length, 1);
  const counts = {
    green: clauses.filter((c) => c.risk_level === "green").length,
    yellow: clauses.filter((c) => c.risk_level === "yellow").length,
    red: clauses.filter((c) => c.risk_level === "red").length,
  };
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(t);
  }, []);
  const w = (n: number) => (mounted ? `${(n / total) * 100}%` : "0%");
  return (
    <div className="scan-dist">
      <div className="scan-dist-head">
        <div className="scan-dist-title">Risk distribution</div>
        <div className="scan-dist-sub">{clauses.length} clauses · click to filter</div>
      </div>
      <div aria-label="Risk distribution — click a segment to filter" className="scan-dist-bar" role="group">
        {counts.green > 0 && (
          <button
            aria-label={`${counts.green} low-risk clauses`}
            className={`scan-dist-seg${active === "green" ? " is-active" : ""}`}
            style={{ width: w(counts.green), background: "var(--green)" }}
            title={`${counts.green} low-risk`}
            type="button"
            onClick={() => onSelect(active === "green" ? "all" : "green")}
          />
        )}
        {counts.yellow > 0 && (
          <button
            aria-label={`${counts.yellow} moderate-risk clauses`}
            className={`scan-dist-seg${active === "yellow" ? " is-active" : ""}`}
            style={{ width: w(counts.yellow), background: "var(--amber)" }}
            title={`${counts.yellow} moderate-risk`}
            type="button"
            onClick={() => onSelect(active === "yellow" ? "all" : "yellow")}
          />
        )}
        {counts.red > 0 && (
          <button
            aria-label={`${counts.red} high-risk clauses`}
            className={`scan-dist-seg${active === "red" ? " is-active" : ""}`}
            style={{ width: w(counts.red), background: "var(--red)" }}
            title={`${counts.red} high-risk`}
            type="button"
            onClick={() => onSelect(active === "red" ? "all" : "red")}
          />
        )}
      </div>
      <div className="scan-dist-legend">
        <button className={`scan-dist-legend-btn${active === "green" ? " is-active" : ""}`} type="button" onClick={() => onSelect(active === "green" ? "all" : "green")}>
          <span className="scan-dist-legend-dot" style={{ background: "var(--green)" }} />Low · {counts.green}
        </button>
        <button className={`scan-dist-legend-btn${active === "yellow" ? " is-active" : ""}`} type="button" onClick={() => onSelect(active === "yellow" ? "all" : "yellow")}>
          <span className="scan-dist-legend-dot" style={{ background: "var(--amber)" }} />Moderate · {counts.yellow}
        </button>
        <button className={`scan-dist-legend-btn${active === "red" ? " is-active" : ""}`} type="button" onClick={() => onSelect(active === "red" ? "all" : "red")}>
          <span className="scan-dist-legend-dot" style={{ background: "var(--red)" }} />High · {counts.red}
        </button>
      </div>
    </div>
  );
}

/* ========================================================================= */
/* ClauseTypeBreakdown — horizontal grouped bars by clause_type               */
/* ========================================================================= */
function ClauseTypeBreakdown({
  clauses,
  onSelectType,
  activeType,
}: {
  clauses: Clause[];
  onSelectType: (t: string | null) => void;
  activeType: string | null;
}) {
  const groups = useMemo(() => {
    const m = new Map<string, { green: number; yellow: number; red: number; total: number }>();
    for (const c of clauses) {
      const k = c.clause_type;
      const g = m.get(k) ?? { green: 0, yellow: 0, red: 0, total: 0 };
      g[c.risk_level] += 1;
      g.total += 1;
      m.set(k, g);
    }
    return Array.from(m.entries())
      .sort((a, b) => b[1].total - a[1].total)
      .slice(0, 6);
  }, [clauses]);
  const maxCount = Math.max(1, ...groups.map(([, g]) => g.total));
  if (groups.length === 0) return null;
  return (
    <div className="scan-types">
      <div className="scan-types-head">
        <div className="scan-types-title">Clauses by type</div>
        <div className="scan-types-sub">Top {groups.length} · click to filter</div>
      </div>
      <div className="scan-types-list">
        {groups.map(([type, g]) => (
          <button
            key={type}
            className={`scan-types-row${activeType === type ? " is-active" : ""}`}
            type="button"
            onClick={() => onSelectType(activeType === type ? null : type)}
          >
            <div className="scan-types-label">{type.replace(/_/g, " ")}</div>
            <div className="scan-types-bar">
              <div className="scan-types-seg" style={{ width: `${(g.green / maxCount) * 100}%`, background: "var(--green)" }} />
              <div className="scan-types-seg" style={{ width: `${(g.yellow / maxCount) * 100}%`, background: "var(--amber)" }} />
              <div className="scan-types-seg" style={{ width: `${(g.red / maxCount) * 100}%`, background: "var(--red)" }} />
            </div>
            <div className="scan-types-count">{g.total}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ========================================================================= */
/* CountStat — animated stat card                                             */
/* ========================================================================= */
function CountStat({ value, label, suffix, delay = 0 }: { value: number; label: string; suffix?: string; delay?: number }) {
  const counted = useCountUp(value, 1100, delay);
  return (
    <div className="scan-stat">
      <div className="scan-stat-n">
        {counted}
        {suffix ? <span className="scan-stat-pct">{suffix}</span> : null}
      </div>
      <div className="scan-stat-l">{label}</div>
    </div>
  );
}

/* ========================================================================= */
/* Inline icons                                                               */
/* ========================================================================= */
const ic = { w: 14, h: 14, fill: "none", stroke: "currentColor", strokeWidth: 1.4, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };

const IconSparkle = () => (
  <svg {...ic} viewBox="0 0 16 16"><path d="M8 1.5v3M8 11.5v3M1.5 8h3M11.5 8h3M3.4 3.4l2.1 2.1M10.5 10.5l2.1 2.1M3.4 12.6l2.1-2.1M10.5 5.5l2.1-2.1" /></svg>
);
const IconChevron = () => (
  <svg fill="none" height="10" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.4" viewBox="0 0 10 10" width="10"><path d="M2 3.5L5 6.5L8 3.5" /></svg>
);
const IconClip = () => (
  <svg {...ic} viewBox="0 0 16 16"><path d="M9.5 3.5L4.2 8.8a2.5 2.5 0 0 0 3.5 3.5L13 7a4 4 0 0 0-5.7-5.7L2 6.6" /></svg>
);
const IconColumns = () => (
  <svg {...ic} viewBox="0 0 16 16"><path d="M1.5 14.5h13M8 1.5L1.5 4.5h13L8 1.5zM3 14.5V7M6 14.5V7M10 14.5V7M13 14.5V7" /></svg>
);
const IconPencil = () => (
  <svg {...ic} viewBox="0 0 16 16"><path d="M11 2.5l2.5 2.5L5 13.5H2.5V11L11 2.5z" /></svg>
);
const IconAtom = () => (
  <svg fill="none" height="16" stroke="currentColor" strokeLinecap="round" strokeWidth="1.4" viewBox="0 0 20 20" width="16"><circle cx="10" cy="10" fill="currentColor" r="1.5" /><ellipse cx="10" cy="10" rx="8" ry="3" /><ellipse cx="10" cy="10" rx="8" ry="3" transform="rotate(60 10 10)" /><ellipse cx="10" cy="10" rx="8" ry="3" transform="rotate(120 10 10)" /></svg>
);
const IconSliders = () => (
  <svg fill="none" height="16" stroke="currentColor" strokeLinecap="round" strokeWidth="1.4" viewBox="0 0 16 16" width="16"><path d="M3 4.5h10M3 8h10M3 11.5h10" /><circle cx="6" cy="4.5" fill="var(--b50)" r="1.4" /><circle cx="10" cy="8" fill="var(--b50)" r="1.4" /><circle cx="5" cy="11.5" fill="var(--b50)" r="1.4" /></svg>
);
const IconMic = () => (
  <svg {...ic} viewBox="0 0 16 16"><rect height="8" rx="2" width="4" x="6" y="2" /><path d="M3 8a5 5 0 0 0 10 0M8 13v1.5M5.5 14.5h5" /></svg>
);
const IconArrowUp = () => (
  <svg fill="none" height="16" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" viewBox="0 0 16 16" width="16"><path d="M8 13V3M4 7l4-4 4 4" /></svg>
);
const IconCheck = () => (
  <svg fill="none" height="12" stroke="var(--cu)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" viewBox="0 0 12 12" width="12"><path d="M2.5 6l2.5 2.5L9.5 3.5" /></svg>
);
const IconClose = () => (
  <svg {...ic} viewBox="0 0 14 14"><path d="M3.5 3.5l7 7M10.5 3.5l-7 7" /></svg>
);
const IconCopy = () => (
  <svg {...ic} viewBox="0 0 16 16"><rect height="9" rx="1.5" width="9" x="5" y="5" /><path d="M3 11V3.5A1.5 1.5 0 0 1 4.5 2H11" /></svg>
);
const IconDownload = () => (
  <svg {...ic} viewBox="0 0 16 16"><path d="M8 2v8.5M4.5 7L8 10.5 11.5 7M2.5 13.5h11" /></svg>
);
const IconShare = () => (
  <svg {...ic} viewBox="0 0 16 16"><circle cx="12" cy="3.5" r="1.8" /><circle cx="4" cy="8" r="1.8" /><circle cx="12" cy="12.5" r="1.8" /><path d="M5.6 7L10.4 4.3M5.6 9L10.4 11.7" /></svg>
);
const IconUpload = () => (
  <svg fill="none" height="36" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.4" viewBox="0 0 32 32" width="36"><path d="M16 20V6M10 12l6-6 6 6M6 24v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2" /></svg>
);
const IconGlobe = () => (
  <svg {...ic} viewBox="0 0 16 16"><circle cx="8" cy="8" r="6" /><path d="M2 8h12M8 2c2 2 3 4 3 6s-1 4-3 6M8 2c-2 2-3 4-3 6s1 4 3 6" /></svg>
);
const IconCloudUp = () => (
  <svg {...ic} viewBox="0 0 16 16"><path d="M4.5 12a3 3 0 0 1-.3-5.98A4.5 4.5 0 0 1 13 7a2.5 2.5 0 0 1-.5 5M8 14V8.5M6 10.5L8 8.5l2 2" /></svg>
);
const IconFolder = () => (
  <svg {...ic} viewBox="0 0 16 16"><path d="M2 4.5A1.5 1.5 0 0 1 3.5 3h2.9l1.5 1.5h4.6A1.5 1.5 0 0 1 14 6v6a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 12V4.5z" /></svg>
);
const IconBook = () => (
  <svg {...ic} viewBox="0 0 16 16"><path d="M2.5 3.5h5A1.5 1.5 0 0 1 9 5v8.5M2.5 3.5V12h5V5M9 5a1.5 1.5 0 0 1 1.5-1.5h3V12M9 13.5h4.5" /></svg>
);

/* ========================================================================= */
/* Main page                                                                  */
/* ========================================================================= */
export default function ScanPage() {
  const [text, setText] = useState("");
  const [language, setLanguage] = useState("en");
  const [jurisdiction, setJurisdiction] = useState("US");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ScanResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [limitReached, setLimitReached] = useState(false);

  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const [deepMode, setDeepMode] = useState(false);
  const [isMac, setIsMac] = useState(false);

  const [filter, setFilter] = useState<FilterMode>("all");
  const [sort, setSort] = useState<SortMode>("risk");
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // Text dropped on Vio from another page. Vio cannot run the scan itself —
  // the scanner and its limits live here — so it parks the text and routes the
  // visitor here, which picks it up exactly once on mount.
  useEffect(() => {
    const handed = takeVioHandoff();
    if (!handed) return;
    setText(handed.slice(0, MAX_CHARS));
    setToast("Vio brought this over — check it and hit scan.");
    setTimeout(() => textareaRef.current?.focus(), 60);
  }, []);
  const [isDragging, setIsDragging] = useState(false);

  const composerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const sourcesBodyRef = useRef<HTMLDivElement>(null);
  const dragCounter = useRef(0);

  const [sourcesMaxHeight, setSourcesMaxHeight] = useState<number | null>(null);

  const charCount = text.length;
  const canSubmit = charCount >= MIN_CHARS && charCount <= MAX_CHARS && !loading;
  const currentLang = LANGUAGES.find((l) => l.value === language) ?? LANGUAGES[0];
  const currentJur = JURISDICTIONS.find((j) => j.value === jurisdiction) ?? JURISDICTIONS[0];

  const wordCount = useMemo(() => (text.trim() ? text.trim().split(/\s+/).length : 0), [text]);
  const readTime = Math.max(1, Math.ceil(wordCount / 220));
  const detectedType = useMemo(() => detectContractType(text), [text]);


  const displayed = useMemo(() => {
    if (!result) return [] as Clause[];
    let list = result.result.clauses;
    if (filter !== "all") list = list.filter((c) => c.risk_level === filter);
    if (typeFilter) list = list.filter((c) => c.clause_type === typeFilter);
    const sorted = [...list];
    if (sort === "risk") sorted.sort((a, b) => b.risk_score - a.risk_score);
    else if (sort === "confidence") sorted.sort((a, b) => b.confidence - a.confidence);
    return sorted;
  }, [result, filter, sort, typeFilter]);

  const visible = showAll ? displayed : displayed.slice(0, INITIAL_CLAUSE_LIMIT);

  const confAvg = useMemo(() => {
    if (!result || !result.result.clauses.length) return 0;
    const sum = result.result.clauses.reduce((s, c) => s + c.confidence, 0);
    return Math.round(sum / result.result.clauses.length);
  }, [result]);

  const lawRefCount = useMemo(() => {
    if (!result) return 0;
    return result.result.clauses.filter((c) => c.india_law_ref).length;
  }, [result]);

  useEffect(() => {
    setIsMac(typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent));
  }, []);

  useEffect(() => {
    if (!openMenu) return;
    const onDown = (e: MouseEvent) => {
      if (composerRef.current && !composerRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpenMenu(null); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [openMenu]);

  // When Sources opens: pin scroll to top AND size the menu so its top edge
  // sits exactly below the navbar. The menu uses `bottom: calc(100% + 6px)`
  // on .scan-menu-sources and is absolutely positioned inside .scan-composer
  // (position:relative), so its bottom edge is at composer.top - 6 — NOT at
  // the Sources chip. Measure the composer.
  useEffect(() => {
    if (openMenu !== "sources") {
      setSourcesMaxHeight(null);
      return;
    }
    if (sourcesBodyRef.current) sourcesBodyRef.current.scrollTop = 0;
    const NAVBAR_H = 62;
    const SAFE_GAP = 8;   // breathing room below navbar
    const MENU_GAP = 6;   // gap between menu bottom and composer top
    const MIN_H = 180;
    const MAX_H = 380;
    const compute = () => {
      const composer = composerRef.current;
      if (!composer) return;
      const composerRect = composer.getBoundingClientRect();
      const available = composerRect.top - NAVBAR_H - SAFE_GAP - MENU_GAP;
      setSourcesMaxHeight(Math.min(MAX_H, Math.max(MIN_H, Math.floor(available))));
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, { passive: true });
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute);
    };
  }, [openMenu]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const h = Math.min(Math.max(el.scrollHeight, 120), 320);
    el.style.height = h + "px";
  }, [text]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2400);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    const hasFiles = (e: DragEvent) => !!e.dataTransfer && e.dataTransfer.types && Array.from(e.dataTransfer.types).includes("Files");
    const onDragEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragCounter.current += 1;
      setIsDragging(true);
    };
    const onDragLeave = (e: DragEvent) => {
      e.preventDefault();
      dragCounter.current -= 1;
      if (dragCounter.current <= 0) {
        dragCounter.current = 0;
        setIsDragging(false);
      }
    };
    const onDragOver = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
    };
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragCounter.current = 0;
      setIsDragging(false);
      setToast("File upload needs a free account — redirecting…");
      setTimeout(() => window.open(`/sign-up`, "_blank", "noopener,noreferrer"), 900);
    };
    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("drop", onDrop);
    };
  }, []);

  // Vio floats above this page and has a `scanning` face — flattened eyes, a
  // beam sweeping the visor. It is wired to this flag rather than played for
  // a second after a drop, so the one state that asserts the product is doing
  // work only appears while the product is actually doing it. Mirrored in an
  // effect rather than set inside runScan so that every exit from the request
  // — success, 429, network error, unmount mid-flight — clears it.
  useEffect(() => {
    setScanning(loading);
    return () => setScanning(false);
  }, [loading]);

  async function runScan() {
    if (!canSubmit) return;
    setOpenMenu(null);
    setLoading(true);
    setError(null);
    setResult(null);
    setLimitReached(false);
    setFilter("all");
    setTypeFilter(null);
    setShowAll(false);
    setTimeout(() => {
      document.getElementById("scan-progress")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
    try {
      const res = await fetch("/api/public-scan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, language, jurisdiction }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 429) {
        setLimitReached(true);
        return;
      }
      if (!res.ok) {
        setError(data?.detail || "Something went wrong. Please try again.");
        return;
      }
      setResult(data as ScanResponse);
      setTimeout(() => {
        document.getElementById("scan-results")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    } catch {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && canSubmit) {
        e.preventDefault();
        void runScan();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSubmit, text, language, jurisdiction]);

  function loadSample(id: string) {
    const tmpl = PROMPT_TEMPLATES.find((t) => t.id === id);
    if (!tmpl) return;
    setText(tmpl.sample);
    setOpenMenu(null);
    setResult(null);
    setError(null);
    setToast(`Loaded: ${tmpl.label}`);
    setTimeout(() => textareaRef.current?.focus(), 60);
  }

  function clearAll() {
    setText("");
    setResult(null);
    setError(null);
    setLimitReached(false);
    textareaRef.current?.focus();
  }

  function improveText() {
    if (!text.trim()) return;
    const prefix =
      "[Deep-analysis request: flag termination risk, auto-renewal, indemnity, jurisdiction, IP assignment, one-sided obligations.]\n\n";
    if (text.startsWith("[Deep-analysis")) return;
    setText(prefix + text);
    setToast("Added deep-analysis directive");
  }

  async function copyReport() {
    if (!result) return;
    const r = result.result;
    const body =
      `LexVio Scan Report\n` +
      `==========================================\n` +
      // EU AI Act Art. 50: content leaving the product must say it is
      // machine-generated — the on-screen disclaimer doesn't travel with a
      // pasted report, so this line does.
      `Machine-generated by LexVio AI — review before relying on it. Not legal advice.\n\n` +
      `Contract type: ${r.contract_type}\n` +
      `Jurisdiction:  ${r.jurisdiction}\n` +
      `Overall risk:  ${r.overall_risk_score}/100 — ${scoreVerdict(r.overall_risk_score)}\n\n` +
      `EXECUTIVE SUMMARY\n${r.executive_summary}\n\n` +
      `CLAUSES (${r.clauses.length})\n` +
      r.clauses
        .map((c, i) => `${i + 1}. [${c.risk_level.toUpperCase()} ${c.risk_score}/10 · conf ${c.confidence}%] ${c.clause_type.replace(/_/g, " ")}\n   "${c.text}"\n   → ${c.explanation}${c.india_law_ref ? `\n   ⚖ ${c.india_law_ref}` : ""}`)
        .join("\n\n") +
      `\n\n${r.disclaimer}`;
    try {
      await navigator.clipboard.writeText(body);
      setToast("Report copied to clipboard");
    } catch {
      setToast("Copy failed — select the text manually");
    }
  }

  function downloadReport() {
    if (!result) return;
    const blob = new Blob([JSON.stringify(result, null, 2)], { type: "application/json" });
    saveBlob(blob, `lexvio-scan-${Date.now()}.json`);
    setToast("Report downloaded as JSON");
  }

  async function shareReport() {
    if (!result) return;
    const r = result.result;
    const shareText = `I ran a ${r.contract_type} through LexVio's free scanner — overall risk ${r.overall_risk_score}/100. Try it: https://lexvio.ai/scan`;
    const nav = navigator as Navigator & { share?: (d: { title?: string; text?: string; url?: string }) => Promise<void> };
    if (nav.share) {
      try {
        await nav.share({ title: "LexVio scan report", text: shareText, url: "https://lexvio.ai/scan" });
        return;
      } catch {
        // fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(shareText);
      setToast("Share link copied");
    } catch {
      setToast("Sharing not supported in this browser");
    }
  }

  const kbd = isMac ? "⌘" : "Ctrl";
  const clauseListTitle =
    filter === "red"    ? "High-risk clauses"
    : filter === "yellow" ? "Moderate-risk clauses"
    : filter === "green"  ? "Low-risk clauses"
    : "Clause-by-clause breakdown";

  return (
    <PageShell>
      <section className="scan-shell">
        <div className="scan-hero">
          <div className="ey rev" style={{ justifyContent: "center" }}>
            <span>Free Scanner</span>
          </div>
          <h1 className="rev d1 scan-hero-h">
            Scan any contract. <em className="scan-hero-em">Free. No login.</em>
          </h1>
          <p className="rev d2 scan-hero-p">
            Paste any contract — NDA, employment, SaaS, rental. Get a clause-by-clause risk report in under 30 seconds, grounded in the governing law the contract names.
          </p>
        </div>

        <div className="scan-composer-wrap">
          <div ref={composerRef} className="scan-composer">
            <div className="scan-composer-top">
              <button
                aria-expanded={openMenu === "prompts"}
                aria-haspopup="menu"
                className={`scan-chip${openMenu === "prompts" ? " is-active" : ""}`}
                type="button"
                onClick={() => setOpenMenu(openMenu === "prompts" ? null : "prompts")}
              >
                <IconSparkle />
                <span>Prompts</span>
                <IconChevron />
              </button>
              <button
                aria-expanded={openMenu === "settings"}
                aria-haspopup="menu"
                className={`scan-chip${openMenu === "settings" ? " is-active" : ""}`}
                title="Change jurisdiction / response language"
                type="button"
                onClick={() => setOpenMenu(openMenu === "settings" ? null : "settings")}
              >
                <Flag code={currentJur.flag} size={14} />
                <span>{currentJur.value} · {currentLang.value.toUpperCase()}</span>
                <IconChevron />
              </button>
            </div>

            <textarea
              ref={textareaRef}
              aria-label="Contract text"
              className="scan-textarea"
              placeholder="Paste any contract — NDA, employment, SaaS terms, rental agreement — or click Prompts to try a sample."
              spellCheck={false}
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, MAX_CHARS))}
            />

            {text.length > 0 && (
              <div className="scan-meter">
                {detectedType && (
                  <span className="scan-meter-detect">
                    <span aria-hidden="true" className="scan-meter-dot" />
                    <span>Detected: <strong>{detectedType}</strong></span>
                  </span>
                )}
                <span className="scan-meter-item">{wordCount.toLocaleString()} words</span>
                <span className="scan-meter-sep">·</span>
                <span className="scan-meter-item">~{readTime} min read</span>
                <span className="scan-meter-spacer" />
                <span className={`scan-meter-count${charCount >= MIN_CHARS ? " is-ok" : ""}`}>
                  {charCount.toLocaleString()} / {MAX_CHARS.toLocaleString()}
                </span>
              </div>
            )}

            <div className="scan-composer-bottom">
              <div className="scan-composer-bottom-l">
                <button disabled className="scan-chip" title="File upload — available with free account" type="button">
                  <IconClip />
                  <span>Files</span>
                </button>
                <button
                  aria-expanded={openMenu === "sources"}
                  aria-haspopup="menu"
                  className={`scan-chip${openMenu === "sources" ? " is-active" : ""}`}
                  type="button"
                  onClick={() => setOpenMenu(openMenu === "sources" ? null : "sources")}
                >
                  <IconColumns />
                  <span>Sources</span>
                </button>
                {text.length >= 30 && (
                  <button
                    className="scan-chip"
                    title="Add a deep-analysis directive to your text"
                    type="button"
                    onClick={improveText}
                  >
                    <IconPencil />
                    <span>Improve</span>
                  </button>
                )}
              </div>

              <div className="scan-composer-bottom-r">
                <button
                  aria-pressed={deepMode}
                  className={`scan-btn-icon${deepMode ? " is-active" : ""}`}
                  title={deepMode ? "Deep research mode — on" : "Deep research mode"}
                  type="button"
                  onClick={() => setDeepMode((v) => !v)}
                >
                  <IconAtom />
                </button>
                <button
                  className={`scan-btn-icon${openMenu === "settings" ? " is-active" : ""}`}
                  title="Settings"
                  type="button"
                  onClick={() => setOpenMenu(openMenu === "settings" ? null : "settings")}
                >
                  <IconSliders />
                </button>
                <span className="scan-divider" />
                <button disabled className="scan-btn-icon" title="Voice input — available with paid plan" type="button">
                  <IconMic />
                </button>
                {text.length > 0 && (
                  <button className="scan-btn-icon" title="Clear" type="button" onClick={clearAll}>
                    <IconClose />
                  </button>
                )}
                <button
                  aria-label="Scan contract"
                  className="scan-send"
                  disabled={!canSubmit}
                  title={canSubmit ? `Scan contract (${kbd}+Enter)` : `Paste at least ${MIN_CHARS} characters`}
                  type="button"
                  onClick={() => void runScan()}
                >
                  {loading ? <span aria-hidden="true" className="scan-send-spin" /> : <IconArrowUp />}
                </button>
              </div>
            </div>

            {openMenu === "prompts" && (
              <div className="scan-menu scan-menu-prompts" role="menu">
                <div className="scan-menu-label">Sample contracts to try</div>
                <div className="scan-menu-body">
                  {PROMPT_TEMPLATES.map((t) => (
                    <button
                      key={t.id}
                      className="scan-menu-item"
                      role="menuitem"
                      type="button"
                      onClick={() => loadSample(t.id)}
                    >
                      <span aria-hidden="true" className="scan-menu-item-ico">{t.icon}</span>
                      <span className="scan-menu-item-body">
                        <span className="scan-menu-item-title">{t.label}</span>
                        <span className="scan-menu-item-sub">{t.hint}</span>
                      </span>
                    </button>
                  ))}
                </div>
                <div className="scan-menu-foot">Loads a ~800-character sample into the composer.</div>
              </div>
            )}

            {openMenu === "sources" && (
              <div
                className="scan-menu scan-menu-sources"
                role="menu"
                style={sourcesMaxHeight ? { maxHeight: `${sourcesMaxHeight}px` } : undefined}
              >
                <div ref={sourcesBodyRef} className="scan-menu-body">
                  <div className="scan-menu-label">Jurisdiction for analysis</div>
                  {JURISDICTIONS.map((j) => (
                    <button
                      key={j.value}
                      aria-checked={j.value === jurisdiction}
                      className={`scan-menu-item${j.value === jurisdiction ? " is-selected" : ""}`}
                      role="menuitemradio"
                      type="button"
                      onClick={() => { setJurisdiction(j.value); setOpenMenu(null); }}
                    >
                      <span aria-hidden="true" className="scan-menu-item-flag"><Flag code={j.flag} size={18} /></span>
                      <span className="scan-menu-item-body">
                        <span className="scan-menu-item-title">{j.label}</span>
                      </span>
                      {j.primary && <span className="scan-menu-tag">Primary</span>}
                      {j.value === jurisdiction && <IconCheck />}
                    </button>
                  ))}
                  <div className="scan-menu-divider" />
                  <div className="scan-menu-label">Add source · free account</div>
                  {[
                    { icon: <IconGlobe />,  label: "Web search" },
                    { icon: <IconCloudUp />, label: "Upload PDF / DOCX" },
                    { icon: <IconFolder />,  label: "Add from Vault" },
                    { icon: <IconBook />,    label: "Add from Knowledge base" },
                  ].map((s) => (
                    <button
                      key={s.label}
                      className="scan-menu-item"
                      type="button"
                      onClick={() => {
                        setOpenMenu(null);
                        setToast(`${s.label} needs a free account — redirecting…`);
                        setTimeout(() => window.open(`/sign-up`, "_blank", "noopener,noreferrer"), 900);
                      }}
                    >
                      <span aria-hidden="true" className="scan-menu-item-ico">{s.icon}</span>
                      <span className="scan-menu-item-body"><span className="scan-menu-item-title">{s.label}</span></span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {openMenu === "settings" && (
              <div className="scan-menu scan-menu-settings" role="menu">
                <div className="scan-settings-block">
                  <h4>Response language</h4>
                  <div className="scan-lang-grid">
                    {LANGUAGES.map((l) => (
                      <button
                        key={l.value}
                        className={`scan-lang-pill${language === l.value ? " is-active" : ""}`}
                        type="button"
                        onClick={() => setLanguage(l.value)}
                      >
                        <span className="scan-lang-native">{l.native}</span>
                        <span className="scan-lang-sub">{l.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="scan-settings-block">
                  <h4>Analysis depth</h4>
                  <div className="scan-segmented">
                    <button className={!deepMode ? "is-active" : ""} type="button" onClick={() => setDeepMode(false)}>Standard</button>
                    <button className={deepMode ? "is-active" : ""} type="button" onClick={() => setDeepMode(true)}>Deep research</button>
                  </div>
                  <div className="scan-settings-hint">
                    Deep research adds case-law cross-references and surfaces ambiguous clauses. Takes 2–3× longer.
                  </div>
                </div>
                <div className="scan-settings-foot">
                  <span>
                    <span style={{ color: charCount >= MIN_CHARS ? "var(--green)" : "rgba(19,18,16,.55)" }}>
                      {charCount.toLocaleString()}
                    </span> / {MAX_CHARS.toLocaleString()} chars
                  </span>
                  <span><kbd>{kbd}</kbd>+<kbd>Enter</kbd> to scan</span>
                </div>
              </div>
            )}
          </div>

          <div className="scan-hint">
            Press <kbd>{kbd}</kbd>+<kbd>Enter</kbd> to scan · Drop a file to upload · 3 free scans per day per IP
          </div>
        </div>

        <div className="scan-rail">
          {[
            // 2026-10-08: was Judgments DB / Statutes / Regulatory filings /
            // Company registry — the research corpus and filings feeds are
            // India-only. These are surfaces a global account gets.
            { label: "Case Studio",       icon: "⚖️", note: "Matter facts & issues" },
            { label: "Privacy",           icon: "📜", note: "Your country's law" },
            { label: "Clause Library",    icon: "🧾", note: "Approved positions" },
            { label: "Client portal",     icon: "🏢", note: "Shares & approvals" },
          ].map((p) => (
            <button
              key={p.label}
              className="scan-rail-item"
              type="button"
              onClick={() => window.open(`/sign-up`, "_blank", "noopener,noreferrer")}
            >
              <span className="scan-rail-item-main">
                <span aria-hidden="true" className="scan-rail-item-ico">{p.icon}</span>
                <span>
                  <span className="scan-rail-item-label">{p.label}</span>
                  <span className="scan-rail-item-note">{p.note}</span>
                </span>
              </span>
              <span className="scan-rail-item-plus">+</span>
            </button>
          ))}
        </div>

        {error && (
          <div className="scan-alert">
            <div>{error}</div>
          </div>
        )}

        {loading && <Pipeline />}

        {limitReached && (
          <div className="scan-limit">
            <div className="scan-limit-title">Daily limit reached.</div>
            <p>You&apos;ve used all 3 free scans today from this IP (25 a month). Create an account to keep going — file uploads, the full report and your vault. No card is taken at sign-up; the joining offer, when one is running, is stated on the sign-up page.</p>
            <a className="btn-p" data-mag="true" href={`/sign-up`} style={{ padding: "14px 32px", fontSize: "15px" }}>
              <span>Create free account →</span>
            </a>
          </div>
        )}

        {result && (
          <div className="scan-results" id="scan-results">
            <div className="scan-result-summary">
              <RiskGauge score={result.result.overall_risk_score} size={132} />
              <div className="scan-result-summary-body">
                <div className="scan-result-top">
                  <div className="scan-result-meta">
                    {result.result.contract_type} · {result.result.jurisdiction}
                    {result.cached ? <span className="scan-badge">cached</span> : null}
                  </div>
                  <div className="scan-actions">
                    <button className="scan-action-btn" title="Copy report as text" type="button" onClick={copyReport}>
                      <IconCopy /><span>Copy</span>
                    </button>
                    <button className="scan-action-btn" title="Download as JSON" type="button" onClick={downloadReport}>
                      <IconDownload /><span>Download</span>
                    </button>
                    <button className="scan-action-btn" title="Share" type="button" onClick={() => void shareReport()}>
                      <IconShare /><span>Share</span>
                    </button>
                  </div>
                </div>
                <div className="scan-result-verdict" style={{ color: scoreColor(result.result.overall_risk_score) }}>
                  {scoreVerdict(result.result.overall_risk_score)}
                </div>
                <p className="scan-result-summary-text">{result.result.executive_summary}</p>
              </div>
            </div>

            <div className="scan-stats">
              <CountStat delay={150} label="Clauses analyzed" value={result.result.clauses.length} />
              <CountStat delay={250} label="Avg confidence" suffix="%" value={confAvg} />
              <CountStat delay={350} label="Law cross-refs" value={lawRefCount} />
            </div>

            <RiskDistribution active={filter} clauses={result.result.clauses} onSelect={(lvl) => { setFilter(lvl); setShowAll(true); }} />

            <ClauseTypeBreakdown
              activeType={typeFilter}
              clauses={result.result.clauses}
              onSelectType={(t) => { setTypeFilter(t); setShowAll(true); }}
            />

            <div aria-label="Clause filters" className="scan-filters" role="tablist">
              <div className="scan-filters-l">
                {(["all", "red", "yellow", "green"] as FilterMode[]).map((m) => (
                  <button
                    key={m}
                    aria-selected={filter === m}
                    className={`scan-filter-pill${filter === m ? " is-active" : ""}`}
                    role="tab"
                    type="button"
                    onClick={() => setFilter(m)}
                  >
                    {m === "all" ? "All" : m === "red" ? "High" : m === "yellow" ? "Moderate" : "Low"}
                    <span className="scan-filter-count">
                      {m === "all"
                        ? result.result.clauses.length
                        : result.result.clauses.filter((c) => c.risk_level === m).length}
                    </span>
                  </button>
                ))}
                {typeFilter && (
                  <button className="scan-filter-chip" title="Clear type filter" type="button" onClick={() => setTypeFilter(null)}>
                    {typeFilter.replace(/_/g, " ")}
                    <IconClose />
                  </button>
                )}
              </div>
              <div className="scan-filters-r">
                <span className="scan-filters-label">Sort</span>
                <div className="scan-segmented scan-sort">
                  <button className={sort === "risk" ? "is-active" : ""} type="button" onClick={() => setSort("risk")}>Risk</button>
                  <button className={sort === "order" ? "is-active" : ""} type="button" onClick={() => setSort("order")}>Order</button>
                  <button className={sort === "confidence" ? "is-active" : ""} type="button" onClick={() => setSort("confidence")}>Conf.</button>
                </div>
              </div>
            </div>

            <div className="scan-clause-head">
              {clauseListTitle}
              {typeFilter && <span className="scan-clause-head-sub"> — {typeFilter.replace(/_/g, " ")}</span>}
              <span className="scan-clause-head-n"> ({displayed.length})</span>
            </div>

            {displayed.length === 0 ? (
              <div className="scan-empty">
                No clauses match the current filter.
                <button className="scan-empty-reset" type="button" onClick={() => { setFilter("all"); setTypeFilter(null); }}>Reset filters</button>
              </div>
            ) : (
              visible.map((c, i) => (
                <div key={`${c.clause_type}-${i}`} className="scan-clause" id={`scan-clause-${i}`} style={{ borderLeftColor: riskColor(c.risk_level) }}>
                  <div className="scan-clause-top">
                    <div className="scan-clause-type" style={{ color: riskColor(c.risk_level) }}>
                      {c.clause_type.replace(/_/g, " ")}
                    </div>
                    <div className="scan-clause-conf">confidence {c.confidence}%</div>
                  </div>
                  <div className="scan-clause-bar" title={`Risk ${c.risk_score}/10`}>
                    <div
                      className="scan-clause-bar-fill"
                      style={{
                        width: `${Math.max(4, (c.risk_score / 10) * 100)}%`,
                        background: riskColor(c.risk_level),
                      }}
                    />
                    <span className="scan-clause-bar-n">{c.risk_score}/10</span>
                  </div>
                  <blockquote className="scan-clause-quote">&ldquo;{c.text}&rdquo;</blockquote>
                  <div className="scan-clause-expl">{c.explanation}</div>
                  {c.india_law_ref && (
                    <div className="scan-clause-law">
                      <span aria-hidden="true">⚖️</span> {c.india_law_ref}
                    </div>
                  )}
                </div>
              ))
            )}

            {displayed.length > INITIAL_CLAUSE_LIMIT && !showAll && (
              <button className="scan-show-all" type="button" onClick={() => setShowAll(true)}>
                Show all {displayed.length} clauses
              </button>
            )}

            <div className="scan-disclaimer">{result.result.disclaimer}</div>

            <div className="scan-cta">
              <div className="scan-cta-body">
                <div className="scan-cta-title">{result.upgrade_cta.message}</div>
                {result.upgrade_cta.offer ? <div className="scan-cta-sub">{result.upgrade_cta.offer}</div> : null}
              </div>
              <a className="btn-p" data-mag="true" href={result.upgrade_cta.url} style={{ padding: "12px 24px", fontSize: "14px" }}>
                <span>Sign up free →</span>
              </a>
            </div>
          </div>
        )}

        <section className="scan-trust">
          <div className="scan-trust-grid">
            {[
              { n: "8–15s", l: "Scan time" },
              { n: "47",    l: "Jurisdictions covered" },
              { n: "3",     l: "Free scans / day" },
              { n: "0",     l: "Account required" },
            ].map((s) => (
              <div key={s.l} className="scan-trust-item">
                <div className="scan-trust-n">{s.n}</div>
                <div className="scan-trust-l">{s.l}</div>
              </div>
            ))}
          </div>
        </section>

        {isDragging && (
          <div aria-hidden="true" className="scan-drag-overlay">
            <div className="scan-drag-overlay-body">
              <IconUpload />
              <div className="scan-drag-overlay-title">Drop to upload</div>
              <div className="scan-drag-overlay-sub">File scanning needs a free account · redirecting…</div>
            </div>
          </div>
        )}

        {toast && (
          <div aria-live="polite" className="scan-toast" role="status">
            <IconCheck /> <span>{toast}</span>
          </div>
        )}
      </section>
    </PageShell>
  );
}
